<template>
  <div class="catalog-tabs-wrap" @change.stop>
    <nav class="catalog-tabs-panel" aria-label="Tabs">
      <hs-tabs :tabs="tabs" :model-value="activeTab" @update:modelValue="handleTabClick" />

      <!-- Right side: search + sort + actions slot -->
      <div class="catalog-tabs-toolbar">
        <label class="catalog-search-field">
          <vue-feather type="search" size="16" />
          <input type="text" v-model="localSearchQuery" @input.stop="handleSearchInput" @change.stop="handleSearchInput"
            @keyup.enter.stop="handleSearchInput" :placeholder="`Search ${activeTab === 'inventory'
              ? 'inventory'
              : activeTab === 'packaging'
                ? 'quantity packaging'
                : activeTab === 'products'
                  ? 'products'
                  : activeTab === 'services'
                    ? 'services'
                    : 'items'
              }`" />
        </label>

        <!-- Inline loader for search debounce or parent loading -->
        <div v-if="loading || searching" class="catalog-toolbar-loader">
          <span aria-label="Loading"></span>
        </div>

        <!-- Sort dropdown for inventory tab (name, quantity only) -->
        <HSCustomSelect v-if="activeTab === 'inventory'" v-model="localSortBy" :options="inventorySortOptions"
          icon="sliders" class="catalog-toolbar-select" @change="$emit('sort-change', $event)" />

        <!-- Sort dropdown for purchases tab (name, quantity, expiration, created) -->
        <HSCustomSelect v-else-if="activeTab === 'purchases'" v-model="localSortBy" :options="purchaseSortOptions"
          icon="sliders" class="catalog-toolbar-select" @change="$emit('sort-change', $event)" />

        <!-- Expiration filter for purchases tab -->
        <HSCustomSelect v-if="activeTab === 'purchases'" v-model="localExpirationFilter"
          :options="expirationFilterOptions" icon="filter"
          class="catalog-toolbar-select catalog-toolbar-select--compact"
          @change="$emit('expiration-filter-change', $event)" />

        <div class="catalog-tab-actions">
          <slot name="actions"></slot>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import HSCustomSelect from "@/components/common/HSCustomSelect.vue";
import { HsTabs } from "@/components/ui";

export default {
  name: "ItemsTabs",
  components: {
    HSCustomSelect,
    HsTabs,
  },
  emits: ["tab-change", "search", "sort-change", "expiration-filter-change"],
  props: {
    activeTab: {
      type: String,
      required: true,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    sortBy: {
      type: String,
      default: "name",
    },
    expirationFilter: {
      type: String,
      default: "all",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localSearchQuery: "",
      // Initialize from prop so current sort reflects immediately
      localSortBy: this.sortBy || "name",
      localExpirationFilter: this.expirationFilter || "all",
      searching: false,
      searchDebounceId: null,
      inventorySortOptions: [
        { value: "name", label: "Sort by Name (A to Z)" },
        { value: "-name", label: "Sort by Name (Z to A)" },
        { value: "quantity", label: "Sort by Quantity (High to Low)" },
        { value: "-quantity", label: "Sort by Quantity (Low to High)" },
      ],
      purchaseSortOptions: [
        { value: "-created_at", label: "Sort by Created (Newest First)" },
        { value: "created_at", label: "Sort by Created (Oldest First)" },
        { value: "name", label: "Sort by Name (A to Z)" },
        { value: "-name", label: "Sort by Name (Z to A)" },
        { value: "quantity", label: "Sort by Quantity (High to Low)" },
        { value: "-quantity", label: "Sort by Quantity (Low to High)" },
        { value: "expiration_date", label: "Sort by Expiration (Oldest First)" },
        { value: "-expiration_date", label: "Sort by Expiration (Newest First)" },
      ],
      expirationFilterOptions: [
        { value: "all", label: "All Items" },
        { value: "hide_expired", label: "Hide Expired Items" },
        { value: "expired_only", label: "Show Expired Items Only" },
      ],
      tabs: [
        { id: "inventory", label: "Inventory", icon: "archive" },
        { id: "packaging", label: "Packaging", icon: "layers" },
        { id: "purchases", label: "Stock", icon: "package" },
        { id: "products", label: "Products", icon: "shopping-bag" },
        { id: "services", label: "Services", icon: "briefcase" },
        { id: "transfer_requests", label: "Transfer Requests", icon: "send" },
        { id: "transfers", label: "Transfers", icon: "repeat" },
        { id: "removals", label: "Removals", icon: "trash-2" },
      ],
    };
  },
  watch: {
    searchQuery(newVal) {
      // Sync the local search query when parent clears the search
      this.localSearchQuery = newVal;
    },
    sortBy: {
      // Sync the local sort when parent changes it, including on initial mount
      immediate: true,
      handler(newVal) {
        this.localSortBy = newVal || "name";
      },
    },
    expirationFilter: {
      immediate: true,
      handler(newVal) {
        this.localExpirationFilter = newVal || "all";
      },
    },
    activeTab(newVal) {
      // Default sorts per tab
      if (newVal === "inventory") {
        this.localSortBy = "name";
        this.$emit("sort-change", "name");
      } else if (newVal === "purchases") {
        this.localSortBy = "-created_at";
        this.$emit("sort-change", "-created_at");
      } else {
        // other tabs: no sort
        this.localSortBy = "name";
      }
      // Reset expiration filter when switching tabs
      if (newVal !== "purchases") {
        this.localExpirationFilter = "all";
      }
    },
  },
  mounted() {
    this.localSearchQuery = this.searchQuery || "";
  },
  methods: {
    handleTabClick(tabId) {
      // Ensure we only emit string values
      if (typeof tabId === "string") {
        this.$emit("tab-change", tabId);
      }
    },
    handleSearchInput(e) {
      const value = typeof e?.target?.value === "string" ? e.target.value : "";
      this.localSearchQuery = value;
      if (this.searchDebounceId) clearTimeout(this.searchDebounceId);
      this.searching = true;
      this.searchDebounceId = setTimeout(() => {
        this.$emit("search", value);
        this.searching = false;
      }, 400);
    },
  },
  beforeUnmount() {
    if (this.searchDebounceId) clearTimeout(this.searchDebounceId);
  },
};
</script>

<style scoped>
.catalog-tabs-wrap {
  margin-bottom: 0.75rem;
  position: relative;
  width: 100%;
}

.catalog-tabs-panel {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
}

.catalog-tabs-toolbar {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: thin;
  width: 100%;
}

.catalog-search-field {
  align-items: center;
  background: var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  color: var(--hs-muted, #64748b);
  display: inline-flex;
  gap: 9px;
  min-height: 42px;
  padding: 0 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.catalog-search-field {
  flex: 0 0 380px;
  min-width: 0;
}

.catalog-toolbar-select {
  flex: 0 0 250px;
}

.catalog-toolbar-select--compact {
  flex-basis: 190px;
}

.catalog-search-field:focus-within,
.catalog-toolbar-select:focus-within {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 48%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
}

.catalog-search-field input {
  background: transparent;
  border: 0;
  color: var(--hs-text, #0f172a);
  flex: 1;
  font-size: 0.88rem;
  font-weight: 700;
  min-width: 0;
  outline: none;
}

.catalog-toolbar-loader {
  align-items: center;
  display: flex;
  height: 42px;
  justify-content: center;
  width: 30px;
}

.catalog-toolbar-loader span {
  animation: spin 0.8s linear infinite;
  border: 2px solid var(--hs-border, rgba(148, 163, 184, 0.24));
  border-radius: 999px;
  border-top-color: var(--hs-primary, #3293ec);
  display: inline-block;
  height: 18px;
  width: 18px;
}

.catalog-tab-actions {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 10px;
  justify-content: flex-end;
  min-width: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (min-width: 768px) {
  .catalog-tabs-wrap {
    position: sticky;
    top: 95px;
    z-index: 30;
  }
}

@media (max-width: 760px) {
  .catalog-tabs-panel {
    border-radius: 18px;
    padding: 10px;
  }

  .catalog-tabs-toolbar,
  .catalog-tab-actions {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    overflow-x: visible;
  }

  .catalog-search-field,
  .catalog-toolbar-select {
    flex-basis: auto;
    max-width: none;
    width: 100%;
  }
}
</style>
